/**
 * EditFormContainer - Renders edit forms as modal or side panel
 *
 * Reads editFormOpen and editFormMode from aircraftCardStore.
 * Looks up form in FORM_REGISTRY and renders inside Dialog or Sheet.
 * Toggle icon in header lets user switch between modal/side panel (persisted).
 * "View Details" button transitions to AircraftFullModal on the relevant tab.
 */

import { PanelRight, Maximize2, Expand } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAircraftCardStore } from '@/stores/aircraftCardStore'
import { useAircraftEditorStore } from '@/stores/aircraftEditorStore'
import { FORM_REGISTRY } from './formRegistry'
import { PlaceholderEditForm } from './PlaceholderEditForm'
import { AllocationSettingsForm } from '@/components/forms'
import { TransientStayForm } from '@/components/transient-stays/TransientStayForm'
import { AircraftForm, mapAircraftDetailToFormValues } from '@/components/aircrafts/AircraftForm'
import { ClientForm } from '@/components/clients'
import type { AllocationEnrichedData } from '@/types/aircraft-card'

interface EditFormContainerProps {
  enrichedData: Map<string, AllocationEnrichedData>
}

export function EditFormContainer({ enrichedData }: EditFormContainerProps) {
  const editFormOpen = useAircraftCardStore((s) => s.editFormOpen)
  const editFormMode = useAircraftCardStore((s) => s.editFormMode)
  const closeEditForm = useAircraftCardStore((s) => s.closeEditForm)
  const setEditFormMode = useAircraftCardStore((s) => s.setEditFormMode)
  const openFullModal = useAircraftCardStore((s) => s.openFullModal)
  const selectedAllocationId = useAircraftEditorStore((s) => s.selectedAllocationId)

  if (!editFormOpen) return null

  const entry = FORM_REGISTRY[editFormOpen.formId]
  const title = entry?.title || 'Edit'

  // Get the data for the selected aircraft
  const selectedData = selectedAllocationId
    ? enrichedData.get(selectedAllocationId)
    : undefined

  // Render the appropriate form based on formId
  const renderForm = () => {
    if (!selectedData) {
      return <PlaceholderEditForm title="No aircraft selected" />
    }

    const { aircraft } = selectedData

    switch (editFormOpen.formId) {
      case 'aircraft-notes':
        return (
          <AircraftForm
            variant="notes"
            aircraftId={aircraft.id}
            initialData={{ notes: aircraft.notes }}
            onSuccess={closeEditForm}
            onCancel={closeEditForm}
          />
        )

      case 'aircraft-details':
        return (
          <AircraftForm
            variant="details"
            aircraftId={aircraft.id}
            initialData={mapAircraftDetailToFormValues(aircraft)}
            prefocusField={editFormOpen.prefocusField}
            onSuccess={closeEditForm}
            onCancel={closeEditForm}
          />
        )

      case 'transient-stay':
        if (!selectedData.transientStay) {
          return <PlaceholderEditForm title="No active transient stay" />
        }
        return (
          <TransientStayForm
            variant="inline"
            stayId={selectedData.transientStay.id}
            initialData={{
              status: selectedData.transientStay.status,
              checkIn: selectedData.transientStay.checkIn,
              expectedCheckOut: selectedData.transientStay.expectedCheckOut,
              rateType: selectedData.transientStay.rateType,
              rateAmount: selectedData.transientStay.rateAmount,
              bookingType: selectedData.transientStay.bookingType,
              notes: selectedData.transientStay.notes,
            }}
            prefocusField={editFormOpen.prefocusField}
            onSuccess={closeEditForm}
            onCancel={closeEditForm}
          />
        )

      case 'allocation-settings':
        return (
          <AllocationSettingsForm
            allocationId={selectedData.allocationId}
            initialData={{
              customLabel: selectedData.allocation.customLabel,
              showLabel: selectedData.allocation.showLabel ?? false,
              colorOverride: selectedData.allocation.colorOverride,
            }}
            prefocusField={editFormOpen.prefocusField}
            onSuccess={closeEditForm}
            onCancel={closeEditForm}
          />
        )

      case 'client-editor':
        if (!selectedData.aircraft.owner) {
          return <PlaceholderEditForm title="No owner assigned" />
        }
        return (
          <ClientForm
            variant="inline"
            clientId={selectedData.aircraft.owner.id}
            initialData={{
              clientType: selectedData.aircraft.owner.clientType,
              firstName: selectedData.aircraft.owner.firstName,
              lastName: selectedData.aircraft.owner.lastName,
              companyName: selectedData.aircraft.owner.companyName,
              legalEntityName: null,
              emailPrimary: selectedData.aircraft.owner.emailPrimary,
              phonePrimary: selectedData.aircraft.owner.phonePrimary,
              phoneSecondary: null,
              addressLine1: null,
              addressLine2: null,
              city: null,
              stateProvince: null,
              postalCode: null,
              country: null,
              paymentTerms: 'net_30',
              taxExempt: false,
              taxId: null,
              status: selectedData.aircraft.owner.status,
              notes: null,
            }}
            prefocusField={editFormOpen.prefocusField}
            onSuccess={closeEditForm}
            onCancel={closeEditForm}
          />
        )

      default:
        return <PlaceholderEditForm title={title} />
    }
  }

  const handleViewDetails = () => {
    const tab = entry?.defaultTab ?? 'aircraft'
    closeEditForm()
    openFullModal(tab)
  }

  const formContent = renderForm()

  const headerActions = (
    <div className="flex items-center gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={handleViewDetails}
          >
            <Expand className="w-3.5 h-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">View all details</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => setEditFormMode(editFormMode === 'modal' ? 'sidePanel' : 'modal')}
          >
            {editFormMode === 'modal' ? (
              <PanelRight className="w-3.5 h-3.5" />
            ) : (
              <Maximize2 className="w-3.5 h-3.5" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {editFormMode === 'modal' ? 'Switch to side panel' : 'Switch to modal'}
        </TooltipContent>
      </Tooltip>
    </div>
  )

  if (editFormMode === 'sidePanel') {
    return (
      <Sheet open onOpenChange={(open) => { if (!open) closeEditForm() }}>
        <SheetContent className="sm:max-w-[420px]">
          <SheetHeader>
            <div className="flex items-center justify-between">
              <SheetTitle>{title}</SheetTitle>
              {headerActions}
            </div>
          </SheetHeader>
          <div className="mt-4">{formContent}</div>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) closeEditForm() }}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{title}</DialogTitle>
            {headerActions}
          </div>
        </DialogHeader>
        <div className="mt-2">{formContent}</div>
      </DialogContent>
    </Dialog>
  )
}
